import { useMemo, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { StatusDot } from '../../components/ui/StatusDot';
import { EstadoVacio } from '../../components/ui/Estados';
import type { Sensor } from '../../../domain/entities/Sensor';

interface SelectorSensorProps {
  abierto: boolean;
  sensores: readonly Sensor[];
  onCerrar: () => void;
  onElegir: (sensor: Sensor) => void;
  /** Permite desvincular el elemento del sensor que tuviera. */
  onQuitar?: () => void;
}

/** Catálogo para elegir a qué sensor apunta un marcador o una tarjeta. */
export function SelectorSensor({
  abierto,
  sensores,
  onCerrar,
  onElegir,
  onQuitar,
}: SelectorSensorProps) {
  const [busqueda, setBusqueda] = useState('');

  const filtrados = useMemo(
    () => sensores.filter((s) => s.coincideCon(busqueda)).slice(0, 80),
    [sensores, busqueda],
  );

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Elegir sensor"
      descripcion="El elemento mostrará el valor de este sensor en vivo."
      pie={
        <>
          {onQuitar && (
            <Button variante="secundario" onClick={onQuitar}>
              Quitar sensor
            </Button>
          )}
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
        </>
      }
    >
      <Input
        etiqueta="Buscar"
        icono="buscar"
        placeholder="Nombre del sensor, nodo o área"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        autoFocus
      />

      <div
        style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.75rem' }}
      >
        {filtrados.length === 0 ? (
          <EstadoVacio titulo="Ningún sensor coincide" descripcion="Prueba con otro nombre." />
        ) : (
          filtrados.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onElegir(s)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                padding: '0.5rem 0.65rem',
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-input)',
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
              }}
            >
              <StatusDot color={s.estado.color} tamano={9} etiqueta={s.estado.etiqueta} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 500 }}>{s.nombre}</span>
                <span style={{ fontSize: 'var(--text-micro)', color: 'var(--text-2)' }}>
                  {[s.areaNombre, s.nodoNombre].filter(Boolean).join(' · ') || 'Sin ubicación'}
                </span>
              </span>
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                {s.valorNumericoMostrable}
                {s.unidadMostrable && (
                  <span style={{ color: 'var(--text-2)', marginLeft: 2 }}>{s.unidadMostrable}</span>
                )}
              </span>
            </button>
          ))
        )}
      </div>
    </Modal>
  );
}
